import { LineName } from '@/formats/analysis-formats';
import Zoom from '@/logic/Zoom';
import type Audio from '../Audio';
import type Metadata from '../Metadata';
import Staff, { StaffType } from '../Staff';
import type Unit from '../Unit';
import type { Pixel } from '../Unit';
import type { Image3DPageData } from './Image3DPageData';
import type { ImageLikeSourceData } from './ImageLikeSourceData';
import type { SourceType } from './SourceType';

export abstract class ImageLikeSource {
  static LINE_HEIGHT = 34;

  static ADDITIONAL_PADDING = 10;

  static MARGIN_NO_PADDING = this.LINE_HEIGHT * 3;

  static TOTAL_MARGIN = this.ADDITIONAL_PADDING + this.MARGIN_NO_PADDING;

  id: number;

  readonly type: SourceType;

  readonly pixelUnit: Unit<Pixel>;

  readonly width: Pixel;

  readonly height: Pixel;

  readonly metadata: Metadata;

  readonly source?: string | undefined;

  readonly name?: string;

  readonly track?: Audio;

  readonly shouldSnap: boolean;

  readonly pages?: Array<Image3DPageData>;

  readonly zoom: Zoom;

  isLinear: boolean;

  alreadyHasDefaultLines = false;

  protected constructor(parsed: ImageLikeSourceData) {
    this.id = 0;
    this.type = parsed.type;
    this.pixelUnit = parsed.pixelUnit;
    this.height = parsed.height;
    this.width = parsed.width;
    this.name = parsed.name;
    this.metadata = parsed.metadata;
    this.source = parsed.source;
    this.track = parsed.track;
    this.pages = parsed.pages;
    this.shouldSnap = parsed.type !== 'wave';
    this.zoom = new Zoom();
    this.isLinear = parsed.isLinear;
  }

  setId(id: number): void {
    this.id = id;
  }

  setZoom(zoom: Zoom): void {
    this.zoom.exponent = zoom.getExponentForValue(zoom.value);
  }

  abstract scaled(scale: number): ImageLikeSource;

  private getLine(
    id: number,
    name: LineName,
    top: number,
    height = ImageLikeSource.LINE_HEIGHT,
  ): Staff {
    return new Staff(
      id,
      top as Pixel,
      (top + height) as Pixel,
      name,
      id ? StaffType.line : StaffType.bar,
    );
  }

  private getLines(): Staff[] {
    const bottomLineOffset = ImageLikeSource.MARGIN_NO_PADDING + this.height;

    return [
      this.getLine(0, LineName.all, 0, this.height + ImageLikeSource.LINE_HEIGHT * 6),
      this.getLine(1, LineName.top1, ImageLikeSource.LINE_HEIGHT * 0),
      this.getLine(2, LineName.top2, ImageLikeSource.LINE_HEIGHT * 1),
      this.getLine(3, LineName.top3, ImageLikeSource.LINE_HEIGHT * 2),
      this.getLine(4, LineName.bot1, ImageLikeSource.LINE_HEIGHT * 0 + bottomLineOffset),
      this.getLine(5, LineName.bot2, ImageLikeSource.LINE_HEIGHT * 1 + bottomLineOffset),
      this.getLine(6, LineName.bot3, ImageLikeSource.LINE_HEIGHT * 2 + bottomLineOffset),
    ];
  }

  addDefaultLines(): void {
    if (this.alreadyHasDefaultLines) return;
    const lines = this.getLines();
    const originalStaves = [
      ...this.pages![0].rows[0].staves.filter((staff) => staff.type === StaffType.staff),
    ];
    this.pages![0].rows[0].staves = [
      ...lines,
      ...originalStaves.map((staff, index) => ({
        ...staff,
        id: index + 7,
        top: (staff.top + ImageLikeSource.MARGIN_NO_PADDING) as Pixel,
        bottom: (staff.bottom + ImageLikeSource.MARGIN_NO_PADDING) as Pixel,
      })),
    ];
    this.pages![0].rows[0].height = (
      (this.pages![0].rows[0].height as number) + ImageLikeSource.LINE_HEIGHT * 6
    ) as Pixel;
    this.alreadyHasDefaultLines = true;
  }

  get staves(): Staff[] {
    return this.pages![0].rows[0].staves;
  }
}
